import { useMemo } from "react";

import Loading from "@/components/Loading";
import { useUpdateURLSearchParams } from "@/hooks/useQuery";
import useSearchParams from "@/hooks/useSearchParams";
import TabBody from "@/pages/Settings/TabBody";
import { useLoadRoles } from "@/queries/profile/roles/useLoadRoles";
import Button from "@/ui/atoms/Button";
import Text from "@/ui/atoms/Text";

import AddNewRoleModal from "./components/RoleModal/AddNewRoleModal";
import DeleteRoleByIdModal from "./components/RoleModal/DeleteRoleByIdModal";
import EditRoleByIdModal from "./components/RoleModal/EditRoleByIdModal";
import RolesTable from "./components/RolesTable";

const RolesTabPage = () => {
  const { data: roles, isLoading } = useLoadRoles();
  const searchParams = useSearchParams();
  const updateParams = useUpdateURLSearchParams();

  const addNewRole = searchParams.get("addNew") === "true";
  const roleEditId = searchParams.get("editId");
  const roleDeleteId = searchParams.get("deleteId");

  const sortedRoles = useMemo(() => {
    if (!roles) return [];
    const copy = [...roles];
    copy.sort((a, b) => a.id - b.id);
    // place owner role at the top
    const ownerIndex = copy.findIndex((role) => role.name === "Owner");
    if (ownerIndex !== -1) {
      const ownerRole = copy.splice(ownerIndex, 1);
      copy.unshift(ownerRole[0]);
    }

    // place administrator roles after owner role
    const adminRoles = copy.filter((role) => role.name === "Administrator");
    if (adminRoles.length) {
      adminRoles.forEach((role) => {
        const adminIndex = copy.findIndex((r) => r.id === role.id);
        if (adminIndex !== -1) {
          const adminRole = copy.splice(adminIndex, 1);
          copy.splice(1, 0, adminRole[0]);
        }
      });
    }

    return copy;
  }, [roles]);

  return (
    <>
      <TabBody>
        <div className="flex flex-col w-full pt-2 gap-6">
          <div className="flex gap-4 mb-6">
            <Text className="w-full leading-10">
              Roles allow you to manage and regulate your team's activities and
              their access to specific actions.
            </Text>
            <Button
              $primary
              className="whitespace-nowrap"
              onClick={() => updateParams(["addNew", "true"])}
            >
              Add New Role
            </Button>
          </div>
          {isLoading && <Loading fullHeight={false} />}
          {!isLoading && <RolesTable loading={isLoading} roles={sortedRoles} />}
        </div>
      </TabBody>
      {addNewRole ? <AddNewRoleModal /> : null}
      {roleDeleteId ? <DeleteRoleByIdModal /> : null}
      {roleEditId ? <EditRoleByIdModal /> : null}
    </>
  );
};

export default RolesTabPage;
